import { useState, type ElementType } from 'react';
import { Star, ChevronRight } from 'lucide-react';
import { useNavigationStore } from '../../stores/navigationStore';
import { Link } from 'react-router-dom';

interface NavItem {
  id: string;
  name: string;
  icon: ElementType;
  path: string;
}

interface NavigationFavoritesProps {
  allNavItems: NavItem[];
}

export function NavigationFavorites({ allNavItems }: NavigationFavoritesProps) {
  const { favorites, removeFavorite, density, incrementUsage } = useNavigationStore();
  const [showAll, setShowAll] = useState(false);

  const favoriteItems = allNavItems.filter((item) => favorites.includes(item.id));
  const displayItems = showAll ? favoriteItems : favoriteItems.slice(0, 5);

  if (favoriteItems.length === 0) {
    return null;
  }

  const paddingClass = {
    compact: 'py-1.5 px-2',
    normal: 'py-2 px-3',
    comfortable: 'py-2.5 px-3',
  }[density];

  const spacingClass = {
    compact: 'space-y-0.5',
    normal: 'space-y-1',
    comfortable: 'space-y-1.5',
  }[density];

  return (
    <div className={`${spacingClass}`}>
      <div className="px-3 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <Star className="w-3.5 h-3.5" />
          <span>Favorites</span>
        </div>
      </div>
      <div className={spacingClass}>
        {displayItems.map((item) => {
          const Icon = item.icon;
          return (
            <div
              key={item.id}
              className="group flex items-center justify-between hover:bg-gray-50 rounded-lg transition"
            >
              <Link
                to={item.path}
                onClick={() => incrementUsage(item.id)}
                className={`flex-1 flex items-center space-x-2 ${paddingClass} text-sm text-gray-700 hover:text-gray-900 rounded-lg`}
              >
                <Icon className="w-4 h-4 text-yellow-500" />
                <span className="truncate">{item.name}</span>
              </Link>
              <button
                onClick={() => removeFavorite(item.id)}
                className="opacity-0 group-hover:opacity-100 p-1 mr-2 text-gray-400 hover:text-red-600 transition"
                title="Remove from favorites"
                aria-label={`Remove ${item.name} from favorites`}
              >
                <Star className="w-3.5 h-3.5 fill-current" />
              </button>
            </div>
          );
        })}
      </div>
      {favoriteItems.length > 5 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full flex items-center justify-center space-x-1 px-3 py-2 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition"
        >
          <span>{showAll ? 'Show Less' : `View All (${favoriteItems.length})`}</span>
          <ChevronRight className={`w-3 h-3 transition-transform ${showAll ? 'rotate-90' : ''}`} />
        </button>
      )}
    </div>
  );
}




























